import { Checkmark20Px } from "@locaisolutions/icons";

import { Refresh } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, Container, Stack } from "@mui/material";
import { useToast, ProductCard } from "@qubit/autoparts";
import moment from "moment";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppDispatch, useAppSelector } from "~/app/store";

import MultiPort from "~/features/autostoreBin/MultiPort";
import FlagBinModal from "~/features/autostorePutaway/modals/FlagBinModal";
import InventoryHoldModal from "~/features/autostorePutaway/modals/InventoryHoldModal";
import { setBinsFlags } from "~/features/cycleCounts/cycleCounts.slice";
import { useClientConfig } from "~/hooks/useClientConfig";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useInactivityResetTimer } from "~/hooks/useInactivityResetTimer";
import { useNavbar } from "~/hooks/useNavbar";
import usePortStatus from "~/hooks/usePortStatus";
import { useShouldListenToGridEvents } from "~/hooks/useShouldListenToGridEvents";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import usePromiseInterval from "~/lib/usePromiseIntervalEffect";
import {
  fetchPortStatus,
  closeBin,
  binNotAtPort,
  closeWorkstation,
  getNextBin,
  setNoMoreBins
} from "~/redux/actions/autostore";
import { clearSelectedCountingTasks } from "~/redux/actions/cycleCounts";
import { clearSelectedVariant } from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";
import { useLazyGetBinQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { usePostCompleteCountingTaskMutation } from "~/redux/warehouse/cycleCountsOverhaul.hooks";
import { useVariantsByIdQuery } from "~/redux/warehouse/variant.hooks";
import { InventoryDto } from "~/types/api";

import CountingTaskInventoryCard from "./CountingTaskInventoryCard";

const mapStateToProps = (state: StoreState) => ({
  clientId: getUserClientId(state.login.profile),
  selectedAutostoreGridId: state.workstations.siteWorkstation?.autostoreGridId,
  selectedPortId: state.workstations.sitePortId,
  usersFulfillmentCenter: state.store.usersFulfillmentCenter,
  thisWorkstationId: state.workstations.siteWorkstation?.id,
  selectedCountingTasks: state.cycleCounts.selectedCountingTasks,
  portStateByPort: state.autostore.portStateByPort,
  /** response from nextEmptyBin call */
  nextEmptyBinByPort: state.autostore.nextEmptyBinByPort,
  siteWorkstation: state.workstations.siteWorkstation,
  siteAllPortIds: state.workstations.siteAllPortIds,
  nextBinInventoryByPort: state.autostore.nextBinInventoryByPort,
  noMoreBins: state.autostore.noMoreBins
});

const connector = connect(mapStateToProps, {
  fetchPortStatus,
  binNotAtPort,
  closeBin,
  closeWorkstation,
  getNextBin,
  clearSelectedVariant,
  setNoMoreBins
});

type PropsFromRedux = ConnectedProps<typeof connector>;
export type CycleCountProps = PropsFromRedux;

export const CycleCountTask = (props: CycleCountProps) => {
  const {
    selectedAutostoreGridId,
    selectedPortId,
    usersFulfillmentCenter,
    portStateByPort,
    nextEmptyBinByPort,
    siteWorkstation,
    thisWorkstationId,
    siteAllPortIds,
    nextBinInventoryByPort,
    selectedCountingTasks,
    noMoreBins
  } = props;

  const binFlaggingEnabled = usersFulfillmentCenter?.binFlaggingEnabled;
  const nextBinInventoryByPortArray = Object.values(nextBinInventoryByPort);

  // hooks
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { successToast, errorToast } = useToast();
  const shouldListenToGridEvents = useShouldListenToGridEvents();
  const isInventoryDateFieldDirty = useAppSelector(
    (state) => state.cycleCountsSlice.isInventoryDateFieldDirty
  );
  const { inv_inventoryDateLabel } = useClientConfig();

  // local state
  const [isInventoryHoldModalOpen, setIsInventoryHoldModalOpen] =
    useState(false);
  const [isFlagBinModalOpen, setIsFlagBinModalOpen] = useState(false);
  const [selectedInventoryRecord, setSelectedInventoryRecord] =
    useState<InventoryDto | null>(null);
  // bin fetching state
  const [portPollingActive, setPortPollingActive] = useState<boolean>(false);
  const [binAtPortSeconds, setBinAtPortSeconds] = useState(0);

  const currentSelectedBin = nextBinInventoryByPortArray.find((bin) =>
    bin.nextBinInventory.some(
      (inv) => inv.inventoryId === selectedInventoryRecord?.inventoryId
    )
  );
  const currentSelectedPortId = currentSelectedBin?.portId;

  const { areAllPortsReady, areSomePortsReady, portStateByPortArray } =
    usePortStatus(portStateByPort, siteAllPortIds, currentSelectedPortId);

  const isSelectedBinReady =
    currentSelectedPortId &&
    portStateByPort[currentSelectedPortId]?.getPortResponse.isReady;

  // inventory adjustment state hooks
  const [changedQuantity, setChangedQuantity] = useState<number | undefined>();
  const [inventoryDate, setInventoryDate] = useState<Date | string | null>(
    null
  );
  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled] =
    useState<boolean>(true);

  // query hooks
  const inventoryIds = useMemo(
    () =>
      selectedCountingTasks?.flatMap(
        (cycleCount) => cycleCount.countingTasks
      ) ?? [],
    [selectedCountingTasks]
  );
  const { data: countingTaskVariantData } = useVariantsByIdQuery({
    variantIds: selectedInventoryRecord ? selectedInventoryRecord.variantId : []
  });
  const [completeCountingTaskPost] = usePostCompleteCountingTaskMutation();
  const [fetchBinInfo] = useLazyGetBinQuery();

  const navigateToCycleCountPageCallback = useCallback(() => {
    dispatch(clearSelectedCountingTasks());
    navigate({
      pathname: `/autostore-cyclecountv2`
    });
  }, [navigate, dispatch]);

  // This function is in the dependency array of the 'inactivityModalCloseCallback' function
  // So we need to wrap it with useCallback to avoid endless re-renders
  const resetPage = useCallback(() => {
    setPortPollingActive(false);
    if (selectedAutostoreGridId && siteAllPortIds) {
      void dispatch(closeWorkstation());
    }
    navigateToCycleCountPageCallback();
    dispatch(setNoMoreBins(false));
  }, [
    selectedAutostoreGridId,
    navigateToCycleCountPageCallback,
    setPortPollingActive,
    dispatch,
    siteAllPortIds
  ]);

  useInactivityResetTimer({
    onInactivityModalClose: resetPage
  });

  // Maps product within bin to product on counting-task table
  const handleBinClickCallback = useCallback(
    (binId?: number, inventoryId?: Guid) => {
      if (binId) {
        if (
          !(selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId === binId)
        ) {
          setChangedQuantity(undefined);
          setInventoryDate(null);
        }

        // Return inventory record which matches on the inventoryId within the selected binId
        const selectedInventoryByBin = nextBinInventoryByPortArray
          .flatMap((inv) => inv.nextBinInventory)
          .filter(
            (binInv) =>
              binInv.bin.autostoreBin?.autostoreBinId === binId &&
              inventoryIds?.includes(binInv.inventoryId)
          );
        // if we know the inventoryId (user clicks specific bin), use it. Otherwise default to first inventoryDto
        const userSelectedInventory = inventoryId
          ? selectedInventoryByBin.find(
              (inv) => inv.inventoryId === inventoryId
            )
          : selectedInventoryByBin[0];
        if (userSelectedInventory) {
          setIsConfirmButtonDisabled(false);
          setSelectedInventoryRecord(userSelectedInventory);
        }
      }
    },
    [
      inventoryIds,
      nextBinInventoryByPortArray,
      selectedInventoryRecord?.bin.autostoreBin?.autostoreBinId
    ]
  );

  const handleClickFlagBin = () => {
    setIsFlagBinModalOpen((prev) => !prev);
    //get all the flags for the current bins and show them above bins on the flagBinModal
    siteAllPortIds?.map((portId) => {
      const binId = portStateByPort[portId].getPortResponse.selectedBin;
      if (binId && selectedAutostoreGridId) {
        void fetchBinInfo({
          autostoreGridId: selectedAutostoreGridId,
          binNumber: binId
        })
          .unwrap()
          .then((binInfo) => {
            dispatch(setBinsFlags({ binId: binId, flags: binInfo.flags }));
          });
      }
    });
  };

  // Port polling: if log publisher enabled, poll port and log publisher state
  const waitInterval = shouldListenToGridEvents ? 7 : 1;
  usePromiseInterval(
    async () => {
      setBinAtPortSeconds((binAtPortSecondsState) => binAtPortSecondsState + 1);
      if (binAtPortSeconds > waitInterval && siteWorkstation) {
        await Promise.all(
          siteWorkstation.ports.map((port) => {
            const portStateResponse =
              portStateByPort[port.portId]?.getPortResponse;
            if (
              !portStateResponse?.isReady &&
              portStateResponse?.selectedBin !== 0
            ) {
              return props.fetchPortStatus({ portId: port.portId });
            }
            return Promise.resolve();
          })
        );
      }
    },
    1000,
    !areAllPortsReady
  );

  const onClickIncreaseQuantity = (previousQty: number) => {
    const newQuantity = (previousQty ?? 0) + 1;
    setChangedQuantity(newQuantity);
  };

  const onClickDecreaseQuantity = (previousQty: number) => {
    const newQuantity = Math.max((previousQty ?? 1) - 1, 0);
    if (newQuantity >= 0) {
      setChangedQuantity(newQuantity);
    } else {
      errorToast("Quantity can't be negative");
    }
  };

  const handleGetNextBin = async (portId: number) => {
    const portState = portStateByPort[portId];
    if (portState?.getPortResponse.selectedBin) {
      await props.closeBin({
        binId: portState.getPortResponse.selectedBin,
        taskId: portState.getPortResponse.selectedTask,
        portId
      });
    }
    await props.getNextBin({ portId, shouldSoftFail: true });
    if (!shouldListenToGridEvents) {
      setPortPollingActive(true);
    } else {
      props.binNotAtPort();
    }

    // Get port status again in order to update portState's ready to be false while calling 'next bin'
    await props.fetchPortStatus({ portId });
  };

  const selectedCountingTask =
    selectedInventoryRecord &&
    selectedCountingTasks?.find((cc) =>
      cc.countingTasks.includes(selectedInventoryRecord.inventoryId)
    );

  const handleCompleteCountingTask = async () => {
    if (
      !selectedInventoryRecord ||
      !selectedInventoryRecord.count.value ||
      !selectedCountingTask ||
      !selectedAutostoreGridId ||
      !currentSelectedPortId ||
      !thisWorkstationId
    ) {
      errorToast("Can't complete task, no grid information found");
      return;
    }

    const getDates = () => {
      const { manufactureDate, expiration } = selectedInventoryRecord;
      let actualManufactureDate;
      let actualExpirationDate;
      if (inv_inventoryDateLabel === "manufacture") {
        const invDate = inventoryDate ?? manufactureDate;
        if (invDate) {
          actualManufactureDate = new Date(invDate);
        }
      } else if (inv_inventoryDateLabel === "expiration") {
        const invDate = inventoryDate ?? expiration;
        if (invDate) {
          actualExpirationDate = new Date(invDate);
        }
      }
      return { actualManufactureDate, actualExpirationDate };
    };
    const { actualManufactureDate, actualExpirationDate } = getDates();

    if (
      countingTaskVariantData?.at(0)?.isExpirationRequired &&
      !actualExpirationDate
    ) {
      errorToast("Expiration Date can't be empty or invalid");
      return;
    }

    try {
      await completeCountingTaskPost({
        cycleCountId: selectedCountingTask.cycleCountId,
        inventoryId: selectedInventoryRecord.inventoryId,
        actualCount: {
          value: changedQuantity ?? selectedInventoryRecord.count.value,
          units: selectedInventoryRecord.count.units
        },
        actualExpiration: actualExpirationDate,
        actualManufactureDate: actualManufactureDate,
        autostoreGridId: selectedAutostoreGridId,
        autostorePortId: currentSelectedPortId,
        workstationId: thisWorkstationId
      }).unwrap();
    } catch (err: unknown) {
      errorToast(getMessageFromRtkError(err));
      return;
    }

    successToast(t("counting task completed successfully"));
    props.clearSelectedVariant();
    setSelectedInventoryRecord(null);
    setIsConfirmButtonDisabled(true);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
    handleGetNextBin(currentSelectedPortId);
  };

  // Starts our Counting Tasks
  useEffect(() => {
    const cycleCountIds = selectedCountingTasks?.map(
      (task) => task.cycleCountId
    );
    if (cycleCountIds?.length && siteAllPortIds) {
      const getAllNextBins = async () => {
        for (const portId of siteAllPortIds) {
          await handleGetNextBin(portId);
        }
      };
      void getAllNextBins();
    }
    // this is intentional, we only want this to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stop polling when bin arrives
  useEffect(() => {
    if (!shouldListenToGridEvents && areAllPortsReady) {
      setPortPollingActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portStateByPort]);

  // auto select port if its the only port
  useEffect(() => {
    if (areAllPortsReady && portStateByPortArray.length === 1) {
      handleBinClickCallback(
        portStateByPortArray[0].getPortResponse.selectedBin
      );
    }
    // we're intentionally leaving out hanleBinClickCallback from the dependency array, including it causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areAllPortsReady, portStateByPortArray.length]);

  // On component dismounting, close the port & cleanup the workstation
  useEffect(() => {
    return () => {
      if (selectedAutostoreGridId && siteAllPortIds) {
        // close workstation
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- TODO: await this
        dispatch(closeWorkstation());
        dispatch(binNotAtPort());
      }
    };
  }, [selectedAutostoreGridId, siteAllPortIds, dispatch]);

  useDevCheats({ isPortPolling: portPollingActive, showAutostoreStatus: true });
  const { setMenuItems } = useNavbar({
    viewTitle: "nav.viewname.autostore cyclecounts"
  });

  useEffect(() => {
    setMenuItems(
      selectedAutostoreGridId && selectedPortId
        ? [
            {
              textContent: t("close workstation"),
              actionCb: resetPage
            }
          ]
        : []
    );
  }, [resetPage, selectedAutostoreGridId, selectedPortId, setMenuItems, t]);

  useEffect(() => {
    if (noMoreBins && nextBinInventoryByPortArray.length === 0) {
      successToast(t("all counting tasks completed successfully"));
      setTimeout(() => {
        // Fire reset page once summariesToDisplay are 0 on page 1
        // The delay allows the incomplete counting tasks projection to be updated
        // so the user does not see the completed task on the cycle count page
        resetPage();
      }, 1200);
    }
  }, [noMoreBins, resetPage, successToast, nextBinInventoryByPortArray, t]);

  const getIsInventoryDateValid = (): boolean => {
    const formattedInventoryDate =
      !moment.isMoment(inventoryDate) && inventoryDate !== null
        ? moment(inventoryDate)
        : inventoryDate;
    const isDateValid =
      isInventoryDateFieldDirty && formattedInventoryDate
        ? formattedInventoryDate.isValid()
        : true;
    if (!isDateValid !== isConfirmButtonDisabled) {
      setIsConfirmButtonDisabled(!isDateValid);
    }
    return isDateValid;
  };

  const selectedCompartment = selectedInventoryRecord?.bin
    .autostoreCompartmentNumber
    ? selectedInventoryRecord.bin.autostoreCompartmentNumber - 1
    : null;

  return (
    <Container maxWidth="xl" sx={{ padding: 2 }}>
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          position="relative"
        >
          <Box position="relative" width="200px">
            <Box position="absolute" right="16px">
              <Button
                onClick={() => resetPage()}
                startIcon={<Refresh />}
                variant="subtle"
              >
                {t("back")}
              </Button>
            </Box>
          </Box>
          <MultiPort
            pickQuantity={0}
            nextBinInventoryByPort={nextBinInventoryByPort}
            nextEmptyBinByPort={nextEmptyBinByPort}
            portStateByPort={portStateByPort}
            workstation={siteWorkstation}
            currentSelectedBinId={
              currentSelectedBin?.nextBinResponse?.openBinResponse.binId
            }
            selectedSummariesAtPort={inventoryIds}
            selectedCompartment={selectedCompartment ?? undefined}
            showLoading={!areSomePortsReady}
            onBinClick={(_, binId) => handleBinClickCallback(binId)}
          />
          <Box position="relative" width="200px">
            <Box position="absolute" left="16px" top="calc(50% - 15px)">
              {binFlaggingEnabled && (
                <Button
                  variant="subtle"
                  onClick={handleClickFlagBin}
                  disabled={!areSomePortsReady}
                >
                  {t("bin problem")}
                </Button>
              )}
            </Box>
          </Box>
        </Stack>
        <Stack direction="column" width="500px" spacing={2} alignSelf="center">
          {selectedCountingTasks?.length &&
          countingTaskVariantData?.length &&
          !!selectedInventoryRecord ? (
            <CountingTaskInventoryCard
              selectedVariant={countingTaskVariantData[0]}
              inv_inventoryDateLabel={inv_inventoryDateLabel}
              inventoryDate={inventoryDate}
              setInventoryDate={setInventoryDate}
              selectedInventoryRecord={selectedInventoryRecord}
              changedQuantity={changedQuantity}
              onClickIncreaseQuantity={onClickIncreaseQuantity}
              onClickDecreaseQuantity={onClickDecreaseQuantity}
              handleChangeQuantity={(count) => setChangedQuantity(count)}
              isDateValid={getIsInventoryDateValid()}
              isCycleCountBlind={selectedCountingTask?.isBlind ?? false}
            />
          ) : (
            <ProductCard.Loading height="540px" disableGutters />
          )}
          <Stack direction="row" spacing={2}>
            <Button
              variant="subtle"
              size="large"
              startIcon={<ErrorOutlineIcon />}
              onClick={() => setIsInventoryHoldModalOpen((prev) => !prev)}
              disabled={!isSelectedBinReady}
              sx={{ px: 6, width: "100%", fontWeight: "normal" }}
            >
              {t("inventory hold")}
            </Button>
            <Button
              size="large"
              sx={{ width: "100%", fontWeight: "normal" }}
              startIcon={<Checkmark20Px style={{ fill: "white" }} />}
              onClick={handleCompleteCountingTask}
              disabled={
                isConfirmButtonDisabled ||
                !isSelectedBinReady ||
                isNaN(changedQuantity ?? 0) ||
                !(changedQuantity || selectedInventoryRecord?.count?.value) ||
                (changedQuantity === undefined && selectedCountingTask?.isBlind) // if blind, user must enter a count before confirming
              }
            >
              {t("confirm")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <InventoryHoldModal
        isOpen={isInventoryHoldModalOpen}
        onClose={() => {
          setIsInventoryHoldModalOpen(false);
        }}
        nextEmptyBinByPort={nextEmptyBinByPort}
        nextBinInventoryByPort={nextBinInventoryByPort}
        portStateByPort={portStateByPort}
        currentSelectedBin={currentSelectedBin?.nextBinResponse ?? null}
        currentSelectedCompartment={selectedCompartment}
      />
      <FlagBinModal
        isOpen={isFlagBinModalOpen}
        onClose={() => setIsFlagBinModalOpen(false)}
        nextBinInventoryByPort={nextBinInventoryByPort}
        nextEmptyBinByPort={nextEmptyBinByPort}
        portStateByPort={portStateByPort}
        workstation={siteWorkstation}
        shouldCloseBinUponSubmit={false}
        selectedBin={currentSelectedBin?.nextBinResponse?.openBinResponse.binId}
        onSuccess={() => {
          successToast(t("flag set successfully"));
        }}
      />
    </Container>
  );
};

export default connector(CycleCountTask);
